import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js';
import { email } from '@vuelidate/validators';

export const MODE = {
  SINGLE: 'single',
  MULTIPLE: 'multiple',
};

export const INPUT_TYPES = {
  EMAIL: 'email',
  TEL: 'tel',
  TEXT: 'text',
};

// Chat Storm: o sistema so atende clientes no Brasil e os usuarios nao sabem que
// precisam digitar o codigo do pais. Com o pais padrao, "99912345678" (DDD +
// telefone) e interpretado como +55 99 91234 5678. Numeros digitados com "+"
// continuam sendo tratados no formato internacional normal.
export const DEFAULT_COUNTRY = 'BR';

export const validatePhoneNumber = value => {
  if (!value) return true;
  try {
    return isValidPhoneNumber(value, DEFAULT_COUNTRY);
  } catch (error) {
    return false;
  }
};

// Chat Storm: numero brasileiro aparece no formato nacional, sem o "+55" que so
// interessa para a API (ex.: "(99) 91234-5678"). Numero de outro pais continua
// no formato internacional, ai o codigo do pais e informacao util.
export const formatPhoneNumberForDisplay = value => {
  try {
    const phoneNumber = parsePhoneNumber(value, DEFAULT_COUNTRY);
    if (!phoneNumber?.isValid()) return value;

    return phoneNumber.country === DEFAULT_COUNTRY
      ? phoneNumber.formatNational()
      : phoneNumber.formatInternational();
  } catch (error) {
    return value;
  }
};

export const formatPhoneNumber = value => {
  try {
    const phoneNumber = parsePhoneNumber(value, DEFAULT_COUNTRY);
    return {
      isValid: phoneNumber?.isValid() || false,
      // Exibicao para o usuario (ex.: "(99) 91234-5678")
      formattedValue: formatPhoneNumberForDisplay(value),
      // Valor que vai para a API, sempre em E.164 (ex.: "+5599912345678")
      e164Value: phoneNumber?.number || value,
    };
  } catch (error) {
    return { isValid: false, formattedValue: value, e164Value: value };
  }
};

export const getValidationRules = type => ({
  newTag: {
    ...(type === INPUT_TYPES.EMAIL ? { email } : {}),
    ...(type === INPUT_TYPES.TEL ? { isValidPhone: validatePhoneNumber } : {}),
  },
});

export const checkTagTypeValidity = (type, value, v$) => {
  if (type === INPUT_TYPES.TEL) {
    return !validatePhoneNumber(value);
  }
  return v$.$invalid;
};

export const validateAndFormatNewTag = (
  trimmedNewTag,
  type,
  isNewTagInValidType
) => {
  let isValid = true;
  let formattedValue = trimmedNewTag;
  // Chat Storm: valor normalizado que sera usado ao criar o contato
  let normalizedValue = trimmedNewTag;

  if (type === INPUT_TYPES.EMAIL) {
    isValid = !isNewTagInValidType;
  } else if (type === INPUT_TYPES.TEL) {
    const {
      isValid: phoneValid,
      formattedValue: phoneFormatted,
      e164Value,
    } = formatPhoneNumber(trimmedNewTag);
    isValid = phoneValid;
    formattedValue = phoneFormatted;
    normalizedValue = e164Value;
  }

  return { isValid, formattedValue, normalizedValue };
};

export const createNewTagMenuItem = (formattedValue, trimmedNewTag, type) => ({
  label: formattedValue,
  value: trimmedNewTag,
  ...(type === INPUT_TYPES.EMAIL ? { email: trimmedNewTag } : {}),
  ...(type === INPUT_TYPES.TEL ? { phoneNumber: trimmedNewTag } : {}),
  thumbnail: { name: formattedValue, src: '' },
  action: 'create',
});

export const buildTagMenuItems = ({
  mode,
  tags,
  menuItems,
  newTag,
  isLoading,
  type,
  isNewTagInValidType,
  allowCreate = true,
  skipLabelDedup = false,
}) => {
  if (mode === MODE.SINGLE && tags.length >= 1) return [];

  const availableMenuItems = skipLabelDedup
    ? menuItems
    : menuItems.filter(item => !tags.includes(item.label));

  // Show typed value as suggestion only if:
  // 1. There's a value being typed
  // 2. The value isn't already in the tags
  // 3. Validation passes (email/phone) and There are no menu items available
  // 4. allowCreate is enabled
  const trimmedNewTag = newTag?.trim();
  const shouldShowCreateSuggestion =
    allowCreate &&
    trimmedNewTag &&
    !tags.includes(trimmedNewTag) &&
    !isLoading &&
    !availableMenuItems.length;

  if (shouldShowCreateSuggestion) {
    const { isValid, formattedValue, normalizedValue } =
      validateAndFormatNewTag(trimmedNewTag, type, isNewTagInValidType);

    if (isValid) {
      // Chat Storm: usa o numero ja normalizado em E.164 para criar o contato
      return [createNewTagMenuItem(formattedValue, normalizedValue, type)];
    }
  }

  return availableMenuItems;
};

export const canAddTag = (mode, tagsLength) =>
  !(mode === MODE.SINGLE && tagsLength >= 1);

// Chat Storm: compara dois telefones pelo formato E.164, ignorando pontuacao e
// o codigo do pais que o usuario pode ou nao ter digitado.
export const isSamePhoneNumber = (phoneA, phoneB) => {
  if (!phoneA || !phoneB) return false;
  const { e164Value: a } = formatPhoneNumber(phoneA);
  const { e164Value: b } = formatPhoneNumber(phoneB);
  return a === b;
};

// Chat Storm: o original so casava por e-mail. Com isso, confirmar um telefone
// com Enter sempre caia no fluxo de "criar contato", mesmo quando o contato ja
// existia na lista. O contato duplicado quebrava a criacao da conversa, porque
// o source_id do canal ja pertencia ao contato original.
export const findMatchingMenuItem = (menuItems, value) =>
  menuItems.find(
    item => item.email === value || isSamePhoneNumber(item.phoneNumber, value)
  );
